import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CurrencyDollarIcon, ShoppingBagIcon, BuildingStorefrontIcon, SignalIcon, PencilIcon, EyeIcon, ExclamationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useBusinessTypeContext } from '../contexts/BusinessTypeContext';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from '../contexts/LocationContext';
import { retailSaleApi, RetailSale } from '../utils/retailSalesApi';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { getErrorMessage } from '../utils/api';

const RetailSales: React.FC = () => {
  const navigate = useNavigate();
  const { businessType } = useBusinessTypeContext();
  const { user } = useAuth();
  const { selectedLocation } = useLocation();
  const [draftSales, setDraftSales] = useState<RetailSale[]>([]);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (selectedLocation?.id && businessType === 'fish') {
      loadDraftSales();
    }
  }, [selectedLocation?.id, businessType]);

  const loadDraftSales = async () => {
    if (!selectedLocation?.id) return;
    
    try {
      setLoadingDrafts(true);
      const params = {
        location_id: selectedLocation.id,
        status: 'draft',
      };
      const data = await retailSaleApi.list(params);
      setDraftSales(data);
    } catch (err: any) {
      console.error('Failed to load draft sales:', err);
    } finally {
      setLoadingDrafts(false);
    }
  };

  const handleContinueDraft = (sale: RetailSale) => {
    // Navigate to form with draftId in URL - form will load from server
    if (sale.sale_type === 'on_farm') {
      navigate(`/retail-sales/on-farm?draftId=${sale.id}`);
    } else {
      navigate(`/retail-sales/market?draftId=${sale.id}`);
    }
  };

  const getStatusBadge = (status: string) => {
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getDisplayAmount = (sale: RetailSale) => {
    if (sale.sale_type === 'on_farm') {
      if (sale.status === 'draft' && sale.cartons && sale.cartons.length > 0) {
        return sale.cartons.reduce((sum, carton) => {
          const qty = Number(carton.quantity_kg) || 0;
          const rate = Number(carton.rate_per_kg) || 0;
          return sum + qty * rate;
        }, 0);
      }
      return sale.total_amount || 0;
    }
    return sale.net_amount || sale.total_amount || 0;
  };

  const handleDiscardDraft = async (saleId: string, saleNumber: string) => {
    if (!window.confirm(`Are you sure you want to discard draft sale "${saleNumber}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeletingId(saleId);
      setError('');
      await retailSaleApi.delete(saleId);
      await loadDraftSales(); // Reload the list
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to discard draft sale'));
    } finally {
      setDeletingId(null);
    }
  };

  // Only show for fish business type
  if (businessType !== 'fish') {
    return (
      <div className="container-mobile py-8">
        <div className="text-center">
          <CurrencyDollarIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Retail Sales</h2>
          <p className="text-gray-600">This feature is available for fish farming only.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="text-center">
        <CurrencyDollarIcon className="h-16 w-16 text-green-600 mx-auto mb-4" />
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Retail Sales</h1>
        <p className="text-lg text-gray-600">
          Manage on-farm and market sales with automatic calculations
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Draft Sales Section - Show at top if any exist */}
      {draftSales.length > 0 && (
        <div className="card p-4 sm:p-6 border-2 border-orange-200 bg-orange-50">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2">
            <div className="flex items-center space-x-2 flex-wrap">
              <ExclamationCircleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-orange-600 flex-shrink-0" />
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 break-words">
                Draft Sales - Continue from here ({draftSales.length})
              </h2>
            </div>
            <Link
              to="/retail-sales/list"
              className="text-sm text-blue-600 hover:text-blue-800 whitespace-nowrap"
            >
              View All →
            </Link>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            You have {draftSales.length} incomplete sale{draftSales.length !== 1 ? 's' : ''}. Click "Continue" to resume editing or "Discard" to delete.
          </p>
          {loadingDrafts ? (
            <div className="flex justify-center py-4">
              <LoadingSpinner size="sm" />
            </div>
          ) : (
            <div className="space-y-3">
              {draftSales.slice(0, 3).map((sale) => (
                <div key={sale.id} className="bg-white rounded-lg p-4 border border-orange-200">
                  <div className="flex flex-col gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className="font-medium text-gray-900 text-sm break-words">{sale.retail_sale_number}</span>
                        {getStatusBadge(sale.status)}
                        <span className="text-xs sm:text-sm text-gray-600 capitalize">
                          {sale.sale_type === 'on_farm' ? 'On-Farm' : 'Market'}
                        </span>
                      </div>
                      <div className="mt-2 text-xs sm:text-sm text-gray-600 break-words">
                        <span className="font-medium">Seller:</span> {sale.seller_name || 'N/A'}
                        {sale.seller_mobile && <span className="ml-1">({sale.seller_mobile})</span>}
                      </div>
                      <div className="mt-1 text-xs sm:text-sm text-gray-600">
                        <span className="font-medium">Date:</span> {new Date(sale.sale_date).toLocaleDateString()}
                        <span className="ml-2 sm:ml-4 font-medium">Amount:</span> ₹{getDisplayAmount(sale).toFixed(2)}
                      </div>
                      {sale.cartons && sale.cartons.length > 0 && (
                        <div className="mt-2 text-xs text-gray-500">
                          {sale.cartons.length} fish crate{sale.cartons.length !== 1 ? 's' : ''} added
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        onClick={() => handleContinueDraft(sale)}
                        className="btn-primary flex items-center justify-center space-x-2 flex-1 min-w-[100px]"
                        title="Continue Editing"
                      >
                        <PencilIcon className="h-4 w-4" />
                        <span className="text-xs sm:text-sm">Continue</span>
                      </button>
                      <button
                        onClick={() => navigate(`/retail-sales/${sale.id}`)}
                        className="text-blue-600 hover:text-blue-800 p-2 flex-shrink-0"
                        title="View Details"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDiscardDraft(sale.id, sale.retail_sale_number)}
                        disabled={deletingId === sale.id}
                        className="text-red-600 hover:text-red-800 p-2 disabled:opacity-50 flex-shrink-0"
                        title="Discard Draft"
                      >
                        {deletingId === sale.id ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <XMarkIcon className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {draftSales.length > 3 && (
                <div className="text-center pt-2">
                  <Link
                    to="/retail-sales/list"
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    View all {draftSales.length} draft sales →
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Sale Type Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* On-Farm Sale */}
        <Link
          to="/retail-sales/on-farm"
          className="card p-6 hover:shadow-lg transition-shadow border-2 border-transparent hover:border-green-500"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <BuildingStorefrontIcon className="h-8 w-8 text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">On-Farm Sale</h3>
              <p className="text-sm text-gray-500">Direct sale at farm</p>
            </div>
          </div>
          <p className="text-sm text-gray-600">
            Record sales when fish seller comes to farm. Track multiple ponds, cartons, 
            weights, and rates. Automatic calculation of totals and amount due.
          </p>
        </Link>

        {/* Market Sale */}
        <Link
          to="/retail-sales/market"
          className="card p-6 hover:shadow-lg transition-shadow border-2 border-transparent hover:border-blue-500"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <ShoppingBagIcon className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Market Sale</h3>
              <p className="text-sm text-gray-500">End-of-day market report</p>
            </div>
          </div>
          <p className="text-sm text-gray-600">
            Record sales when fish is sent to market. Track sold quantity, returned quantity, 
            expenses (polybags, transport), and calculate net amount.
          </p>
        </Link>
      </div>

      {/* Live Monitor Card - Admin Only */}
      {user?.role === 'admin' && (
        <div className="card p-6 bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200">
          <Link
            to="/retail-sales/live"
            className="flex items-center justify-between hover:opacity-80 transition-opacity"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <SignalIcon className="h-8 w-8 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Live Sales Monitor</h3>
                <p className="text-sm text-gray-600">
                  Watch sales happening at the farm in real-time (Admin Only)
                </p>
              </div>
            </div>
            <div className="text-green-600 font-medium">View Live →</div>
          </Link>
        </div>
      )}

      {/* Quick Actions */}
      <div className="flex justify-center space-x-4">
        <Link
          to="/retail-sales/list"
          className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
        >
          View All Sales
        </Link>
        <Link
          to="/retail-sales/sellers"
          className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Manage Sellers
        </Link>
        <Link
          to="/dashboard"
          className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Back to Dashboard
        </Link>
      </div>
    </div>
  );
};

export default RetailSales;

